import { useState, useEffect } from "react";
import axios from "axios";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Code2, CornerDownLeft, ExternalLink, Loader2, AlertTriangle, Database } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  "Where is a timesheet marked as approved?",
  "What happens when an allocation is deleted?",
  "Which table holds `LoadNotes` and what are its columns?",
];

// The model is told to cite as path/to/file.py:120 — turn those into something
// the eye can find in a paragraph, so a claim and its evidence stay joined up.
const renderAnswer = (text) =>
  String(text).split("\n").map((line, i) => {
    if (!line.trim()) return <div key={i} className="h-2" />;
    const parts = line.split(/(`[^`]+`|[\w./-]+\.(?:py|jsx?|tsx?|dart|go|rb|java|cs|sql|yml|yaml|json|md):\d+)/g);
    return (
      <p key={i} className="text-[13px] leading-[1.65] text-[#E8E6E1]">
        {parts.map((p, j) =>
          /^`.+`$/.test(p) || /:\d+$/.test(p) ? (
            <code key={j} className="mono text-[12px] px-1 py-0.5 rounded bg-[#242424] text-[#FFD108]">
              {p.replace(/`/g, "")}
            </code>
          ) : (
            <span key={j}>{p}</span>
          )
        )}
      </p>
    );
  });

export const AskTheCodeDrawer = ({ open, onOpenChange }) => {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ask_code_products") || "[]"); }
    catch { return []; }
  });

  // Which products an agent works on rarely changes between questions, so the
  // choice is remembered rather than re-ticked each time the drawer opens.
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/github/products`)
      .then(({ data }) => setProducts(data.products || []))
      .catch(() => setProducts([]));
  }, [open]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("ask_code_products", JSON.stringify(next));
      return next;
    });
  };

  const ask = async (q) => {
    const text = (q ?? question).trim();
    if (text.length < 5 || busy) return;
    setQuestion(text);
    setBusy(true);
    setResult(null);
    try {
      const body = { question: text };
      if (picked.length) body.products = picked;
      const { data } = await axios.post(`${API}/github/ask`, body, { timeout: 120000 });
      setResult(data);
    } catch (e) {
      setResult({ warnings: [e?.response?.data?.detail || "Could not reach the code."],
        sources: [], answer: "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" data-testid="ask-code-drawer"
        className="w-full sm:max-w-[640px] bg-[#111111] border-l border-[#2B2B2B] overflow-y-auto p-0">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#2B2B2B]">
          <SheetTitle className="flex items-center gap-2 text-[#F5F5F5]">
            <Code2 className="w-5 h-5 text-[#FFD108]" /> Ask The Code
          </SheetTitle>
          <p className="text-[12px] text-[#8A8A8A] text-left">
            Read live from the repositories approved in Administration, read-only.
            Internal use — never paste code or file names into a customer reply.
          </p>
        </SheetHeader>

        <div className="px-5 py-4 space-y-3">
          {products.length > 0 && (
            <div data-testid="ask-code-products">
              <p className="text-[11px] uppercase tracking-[0.08em] text-[#8A8A8A]">
                Which product?
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                <button onClick={() => { setPicked([]); localStorage.setItem("ask_code_products", "[]"); }}
                  data-testid="ask-code-product-all"
                  className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold transition-colors"
                  style={picked.length === 0
                    ? { background: "#FFD108", color: "#121212" }
                    : { background: "#1A1A1A", color: "#B8B4AC", border: "1px solid #2B2B2B" }}>
                  All products
                </button>
                {products.map((p) => (
                  <button key={p.id} onClick={() => toggle(p.id)}
                    data-testid={`ask-code-product-${p.id}`}
                    title={p.kind === "folder"
                      ? `${p.hint} inside ${p.within}`
                      : (p.pushed_at ? `last change ${p.pushed_at}` : p.id)}
                    className="px-2.5 py-1 rounded-full text-[11.5px] font-semibold transition-colors inline-flex items-center gap-1"
                    style={picked.includes(p.id)
                      ? { background: "#FFD108", color: "#121212" }
                      : { background: "#1A1A1A", color: "#B8B4AC", border: "1px solid #2B2B2B" }}>
                    {p.kind === "folder" && <Database className="w-3 h-3" />}
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#6E6E6E] mt-1.5">
                {picked.length === 0
                  ? `Searching all ${products.length} approved products.`
                  : `Searching ${picked.length} of ${products.length} — a ticket about one product rarely lives in another.`}
              </p>
            </div>
          )}
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask();
            }}
            placeholder="Ask about a function, screen, error message or file…"
            rows={3}
            data-testid="ask-code-input"
            className="bg-[#1A1A1A] border-[#2B2B2B] text-[#F5F5F5] text-[13px] resize-none" />
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] text-[#6E6E6E]">⌘/Ctrl + Enter to ask</span>
            <Button onClick={() => ask()} disabled={busy || question.trim().length < 5}
              data-testid="ask-code-submit"
              className="bg-[#FFD108] text-black hover:bg-[#E0B800] font-semibold">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <CornerDownLeft className="w-4 h-4 mr-2" />}
              {busy ? "Reading the code…" : "Ask"}
            </Button>
          </div>

          {!result && !busy && (
            <div className="pt-2 space-y-1.5" data-testid="ask-code-examples">
              {EXAMPLES.map((ex) => (
                <button key={ex} onClick={() => ask(ex)}
                  className="block w-full text-left text-[12.5px] text-[#B8B4AC] hover:text-[#FFFFFF] px-3 py-2 rounded bg-[#1A1A1A] border border-[#2B2B2B] transition-colors">
                  {ex}
                </button>
              ))}
            </div>
          )}
        </div>

        {result && (
          <div className="px-5 pb-8 space-y-4" data-testid="ask-code-result">
            {(result.warnings || []).map((w, i) => (
              <div key={i} data-testid={`ask-code-warning-${i}`}
                className="flex gap-2 text-[12.5px] text-[#E8C46A] bg-[#241F0E] border border-[#4A3C12] rounded px-3 py-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{w}</span>
              </div>
            ))}

            {result.answer && (
              <div className="space-y-1" data-testid="ask-code-answer">
                {renderAnswer(result.answer)}
              </div>
            )}

            {(result.sources || []).length > 0 && (
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-[#8A8A8A] font-semibold">
                  Code it read ({result.sources.length})
                </p>
                {result.sources.map((s, i) => (
                  <div key={`${s.repo}${s.path}`} data-testid={`ask-code-source-${i}`}
                    className="rounded border border-[#2B2B2B] bg-[#161616] overflow-hidden">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#2B2B2B]">
                      <span className="mono text-[11.5px] text-[#D8D4CC] truncate">
                        {s.path}<span className="text-[#6E6E6E]">:{s.line}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-[10.5px] text-[#8A8A8A]">{s.repo.split("/").pop()}</span>
                        {s.url && (
                          <a href={s.url} target="_blank" rel="noreferrer"
                            data-testid={`ask-code-source-link-${i}`}
                            className="text-[#FFD108] hover:underline">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </span>
                    </div>
                    {(s.fragments || []).map((f, j) => (
                      <pre key={j} className="mono text-[11px] text-[#9FD8A0] px-3 py-1.5 overflow-x-auto whitespace-pre">
                        {f}
                      </pre>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {(result.searched_products || []).length > 0 && (
              <p className="text-[11px] text-[#6E6E6E]" data-testid="ask-code-scope">
                Searched live: {result.searched_products.join(", ")}.
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
